/**
 * StarPrint service — the single entry point to the Star printer backend.
 *
 * UI and stores talk to the exported `starPrintService` singleton, never to a
 * printer implementation directly.
 *
 * **The backend is injected, not imported** (PLAN §5.4, A6). `getPlatformPrinter()`
 * resolves it from the capability table: the Capacitor plugin on a phone, the
 * browser mock on the web, and the **Star LAN backend over TCP 9100 in the
 * Electron main process** on a desktop register.
 *
 * That indirection is hazard **F2**'s countermeasure, not a style choice. If
 * this module imported `@am3nd-org/capacitor-starprint-plugin` directly, then on
 * Electron `registerPlugin` would resolve `StarPrintWeb` — an in-memory mock
 * that advertises "Mock LAN mC-Print3" and resolves every print as a success.
 * A register that believes it printed a receipt it never printed is worse than
 * one that cannot print, so on desktop the plugin is never loaded at all.
 *
 * The new plugin is connection-oriented (discover → connect → connectionId →
 * print/status/drawer by connectionId → disconnect), unlike the old stateless
 * Cordova plugin. This service owns one active connection at a time, lazily
 * reconnects from the persisted default printer, and recovers from a stale
 * connection with a single reconnect + retry.
 *
 * All plugin calls are serialized through the shared `withLock` mutex
 * (`src/utils/printer-lock.ts`) — the same lock the legacy path uses — so the
 * old 30s readiness poller and the new path can never hit the hardware at once.
 */
import { withLock } from 'src/utils/printer-lock';
import {
  getStarPrintersFromDB,
  saveDefaultStarPrinterToDB,
  getDefaultStarPrinterFromDB,
  deleteDefaultStarPrinterFromDB,
} from 'src/utils/Services/DB_Utils';

import { getPlatformPrinter } from 'src/platform/services';
import type { StarPrintBackend, PrinterAvailability } from 'src/platform/services';

import type { SavedStarPrinter } from './printer-types';
import { DEFAULT_DISCOVERY_INTERFACES } from './star-print.types';
import type {
  StarPrinter,
  StarPrintResult,
  StarPrinterStatus,
  StarPrinterInterface,
  StarPrintReceiptItem,
  StarPrinterConnection,
  PrinterDiscoveryResult,
  DiscoverPrintersResult,
  StarPrintOperationResult,
  PrinterStatusChangedEvent,
  StarPrintConfigurationIssue,
  BluetoothAuthorizationResult,
} from './star-print.types';

// Per-call timeouts, kept under the lock's 30s auto-release so a single
// connect+print sequence cannot be interrupted mid-operation.
const DISCOVERY_TIMEOUT_MS = 8_000;
const CONNECT_TIMEOUT_MS = 10_000;
const OP_TIMEOUT_MS = 15_000;

// Error codes/messages that mean "the connection went away" → reconnect once.
const STALE_CODES = new Set([
  'NOT_CONNECTED',
  'not_connected',
  'CONNECTION_FAILED',
  'connection_failed',
]);

class StarServiceError extends Error {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'StarServiceError';
    this.code = code;
  }
}

function isStaleError(errLike: unknown): boolean {
  if (!errLike) return false;
  const obj = errLike as { code?: unknown; message?: unknown };
  const code = typeof obj.code === 'string' ? obj.code : '';
  const message = typeof obj.message === 'string' ? obj.message : String(errLike);
  if (STALE_CODES.has(code)) return true;
  return /not connected|fail to open port|disconnected/i.test(message);
}

function toError(result: StarPrintResult, fallback: string): StarServiceError {
  const message = result.error?.message || result.message || fallback;
  return new StarServiceError(result.error?.code ?? 'UNKNOWN', message);
}

/**
 * Map a discovered `StarPrinter` onto the persisted `SavedPrinter` shape. The
 * full `connection` is kept so the service can reconnect headlessly (LAN needs
 * ip/port, Bluetooth the MAC, USB the port name).
 */
export function toSavedPrinter(printer: StarPrinter): SavedStarPrinter {
  return {
    driver: 'star',
    identifier: printer.identifier,
    model: printer.model,
    name: printer.name ?? printer.modelName,
    interfaceType: printer.interfaceType,
    connection: printer.connection,
    savedAt: new Date().toISOString(),
    schemaVersion: 1,
  };
}

/**
 * What a printer that is **not yet saved** said about itself when Add Printer
 * checked it. Its connection has already been released when this resolves.
 */
export interface StarCandidateCheck {
  readonly success: boolean;
  /** The backend's own description of the endpoint (on desktop, main's record). */
  readonly printer?: StarPrinter;
  readonly status?: StarPrinterStatus;
  readonly message?: string;
  readonly error?: StarPrintResult['error'];
}

export interface DiscoverOptions {
  interfaceTypes?: StarPrinterInterface[];
  timeoutMs?: number;
  includeCached?: boolean;
  /** Manual-IP entries. Defaults to whatever `star_printers_v2` remembers. */
  knownAddresses?: string[];
  /** Run the desktop backend's bounded subnet sweep. Ignored on mobile. */
  sweepSubnet?: boolean;
}

/**
 * The LAN addresses already remembered in `star_printers_v2`.
 *
 * A desktop register finds its printer by manual IP first and by a bounded
 * sweep second, so the addresses the operator already saved are the highest-
 * value input a scan can have. Reading them here keeps every existing caller
 * of `discoverPrinters()` unchanged.
 */
async function savedLanAddresses(): Promise<string[]> {
  try {
    const state = await getStarPrintersFromDB();
    return (state?.printers ?? [])
      // Only Star endpoints: a generic ESC/POS printer must never be asked
      // Star's status questions, and it is found by its own driver's scan.
      .filter((printer) => printer.driver !== 'escpos' && printer.interfaceType === 'lan')
      .map((printer) => {
        const connection = printer.connection as { ipAddress?: string; address?: string; port?: number };
        const host = connection.ipAddress ?? connection.address;
        if (host === undefined) return null;
        return connection.port === undefined ? host : `${host}:${String(connection.port)}`;
      })
      .filter((address): address is string => address !== null);
  } catch (error) {
    console.warn('[StarPrintService] could not read saved printer addresses:', error);
    return [];
  }
}

export interface PrintReceiptOptions {
  cutPaper?: boolean;
  openCashDrawer?: boolean;
  copies?: number;
  jobId?: string;
  /** Rechecked after acquiring the printer lock and before each native print attempt. */
  isCurrent?: () => boolean;
}

export interface PrintImageOptions {
  cutPaper?: boolean;
  /** Print width in dots; defaults to the printer's paper width when omitted. */
  width?: number;
  openCashDrawer?: boolean;
  jobId?: string;
  /** Rechecked after acquiring the printer lock and before each native print attempt. */
  isCurrent?: () => boolean;
}

function createStarPrintService() {
  // ── The injected backend ──────────────────────────────────────────────────
  // Resolved once, lazily, and cached as the *promise* so concurrent callers
  // share one resolution. A rejection is not cached: a desktop register whose
  // shell bridge was not ready yet must be able to try again rather than be
  // stuck printing nothing for the rest of the session.
  let backendPromise: Promise<StarPrintBackend> | null = null;

  const backend = (): Promise<StarPrintBackend> => {
    if (backendPromise !== null) return backendPromise;
    const pending = getPlatformPrinter().resolveBackend();
    backendPromise = pending;
    pending.catch(() => {
      if (backendPromise === pending) backendPromise = null;
    });
    return pending;
  };

  // ── Connection state ──────────────────────────────────────────────────────
  let activeConnectionId: string | null = null;
  let activePrinter: StarPrinter | null = null;
  let lastStatus: StarPrinterStatus | null = null;
  let printerSetupUnconfirmed = false;
  let pluginListenersRegistered = false;
  type StatusListener = (status: StarPrinterStatus, connectionId?: string) => void;
  const statusListeners = new Set<StatusListener>();

  /**
   * `connectionId` names the connection the status is about: the desktop LAN
   * backend's `lan:<ip>:<port>` endpoint, or the mobile SDK's opaque handle.
   * The shared printer service uses it to drop events for a printer that is
   * no longer the active one.
   */
  const emit = (status: StarPrinterStatus, connectionId?: string): void => {
    statusListeners.forEach((cb) => {
      try {
        cb(status, connectionId);
      } catch (err) {
        console.error('[StarPrintService] status listener threw:', err);
      }
    });
  };

  // ── Internals (no locking — callers hold the lock) ────────────────────────
  const ensurePluginListeners = async (): Promise<void> => {
    if (pluginListenersRegistered) return;
    pluginListenersRegistered = true;
    const impl = await backend();
    await impl.addListener('printerStatusChanged', (event: PrinterStatusChangedEvent) => {
      // A late status from a discarded setup connection cannot make it active again.
      if (event.connectionId && event.connectionId !== activeConnectionId) return;
      lastStatus = event.status;
      emit(event.status, event.connectionId);
    });
    await impl.addListener('printerDisconnected', () => {
      activeConnectionId = null;
      activePrinter = null;
    });
  };

  const readStatus = async (connectionId: string): Promise<StarPrinterStatus> => {
    const status = await (await backend()).getStatus({ connectionId, timeoutMs: OP_TIMEOUT_MS });
    lastStatus = status;
    emit(status, connectionId);
    return status;
  };

  const connectRaw = async (connection: StarPrinterConnection): Promise<StarPrintResult> => {
    const result = await (await backend()).connect({
      ...connection,
      timeoutMs: connection.timeoutMs ?? CONNECT_TIMEOUT_MS,
    });
    if (result.success && result.connectionId) {
      activeConnectionId = result.connectionId;
      activePrinter = result.printer ?? activePrinter;
      if (result.status) {
        lastStatus = result.status;
        emit(result.status, result.connectionId);
      }
      await ensurePluginListeners();
    }
    return result;
  };

  const ensureConnectedRaw = async (): Promise<string> => {
    if (printerSetupUnconfirmed)
      throw new StarServiceError('SETUP_UNCONFIRMED', 'Ask an administrator to select the receipt printer again.');
    if (activeConnectionId) return activeConnectionId;
    const saved = await getDefaultStarPrinterFromDB();
    if (!saved) {
      throw new StarServiceError('NOT_CONNECTED', 'No default printer configured');
    }
    const result = await connectRaw(saved.connection);
    if (!result.success || !result.connectionId) {
      throw toError(result, 'Could not connect to the saved printer');
    }
    return result.connectionId;
  };

  /** Run an op against the active connection; on a stale connection, reconnect once and retry once. */
  const withConnectionRaw = async (
    run: (connectionId: string) => Promise<StarPrintResult>,
    isCurrent?: () => boolean
  ): Promise<StarPrintOperationResult> => {
    const cancelled = (): StarPrintOperationResult => ({ success: false, cancelled: true });
    // This function runs inside the printer mutex. A queued job can outlive its customer.
    if (isCurrent && !isCurrent()) return cancelled();
    const id = await ensureConnectedRaw();
    const dispatch = (connectionId: string): Promise<StarPrintOperationResult> =>
      isCurrent && !isCurrent() ? Promise.resolve(cancelled()) : run(connectionId);
    const reconnectAndRetry = async (): Promise<StarPrintOperationResult> => {
      if (isCurrent && !isCurrent()) return cancelled();
      return dispatch(await ensureConnectedRaw());
    };
    try {
      const result = await dispatch(id);
      if (!result.success && isStaleError(result.error)) {
        activeConnectionId = null;
        return await reconnectAndRetry();
      }
      return result;
    } catch (err) {
      if (isStaleError(err)) {
        activeConnectionId = null;
        return reconnectAndRetry();
      }
      throw err;
    }
  };

  const disconnectRaw = async (): Promise<void> => {
    const id = activeConnectionId;
    activeConnectionId = null;
    activePrinter = null;
    if (!id) return;
    try {
      await (await backend()).disconnect({ connectionId: id });
    } catch (err) {
      // Disconnect may fail if the connection is already gone — safe to ignore.
      console.warn('[StarPrintService] disconnect failed (ignored):', err);
    }
  };

  /**
   * Closes a candidate's connection and nobody else's. A live job can only
   * hold a connection here after the lock auto-released; when it holds the
   * very same endpoint, or when a failed connect left no id to name, the
   * candidate is left alone rather than risk closing that job's session.
   */
  const releaseCandidate = async (
    impl: StarPrintBackend,
    connection: StarPrinterConnection,
    candidateId: string | undefined
  ): Promise<void> => {
    if (candidateId !== undefined && candidateId === activeConnectionId) return;
    if (candidateId === undefined && activeConnectionId !== null) return;
    try {
      // A failed connect can still leave a registration behind (the desktop
      // backend registers before it opens), so it is released by endpoint.
      await impl.disconnect(candidateId !== undefined ? { connectionId: candidateId } : { connection });
    } catch (err) {
      console.warn('[StarPrintService] candidate release failed (ignored):', err);
    }
  };

  /**
   * Runs one Add Printer step — the check or the test page — against a printer
   * that is **not** this register's printer, then releases it, all inside one
   * lock.
   *
   * The candidate never becomes the live connection: its id is never written
   * to `activeConnectionId`, its status never reaches `lastStatus` or the
   * status listeners, and the shared reconnect never adopts it. A receipt that
   * queues behind this step therefore reconnects the saved printer — even if
   * the lock's 30 s auto-release fires while the candidate is still working.
   * `connectRaw` is deliberately not used here: it would do all three.
   */
  const withCandidate = <T>(
    connection: StarPrinterConnection,
    run: (impl: StarPrintBackend, connectionId: string, connected: StarPrintResult) => Promise<T>,
    failed: (connected: StarPrintResult) => T
  ): Promise<T> =>
    withLock(async () => {
      const impl = await backend();
      // A native connect closes the saved session anyway; forgetting its id
      // makes the next job reconnect the saved printer instead of a dead handle.
      activeConnectionId = null;
      activePrinter = null;
      let candidateId: string | undefined;
      try {
        const connected = await impl.connect({
          ...connection,
          timeoutMs: connection.timeoutMs ?? CONNECT_TIMEOUT_MS,
        });
        if (!connected.success || !connected.connectionId) return failed(connected);
        candidateId = connected.connectionId;
        return await run(impl, candidateId, connected);
      } finally {
        await releaseCandidate(impl, connection, candidateId);
      }
    });

  // ── Public surface ────────────────────────────────────────────────────────
  return {
    /**
     * Discover reachable printers (defaults to LAN + Bluetooth; BLE/USB are opt-in)
     * plus any per-interface `configurationIssues` the native layer reports (e.g. a
     * skipped interface or a permission problem), so callers can explain an empty result.
     *
     * The desktop LAN backend has no SDK browsing the network for it, so it is
     * also handed the addresses already in `star_printers_v2` — that is what
     * makes a printer the operator added by IP reappear on every later scan.
     */
    discoverPrinters(opts: DiscoverOptions = {}): Promise<PrinterDiscoveryResult> {
      return withLock(async () => {
        const impl = await backend();
        const result = await impl.discoverPrinters({
          interfaceTypes: (opts.interfaceTypes ?? DEFAULT_DISCOVERY_INTERFACES).filter(
            (interfaceType) => impl.supportedInterfaces.includes(interfaceType)
          ),
          timeoutMs: opts.timeoutMs ?? DISCOVERY_TIMEOUT_MS,
          includeCached: opts.includeCached,
          knownAddresses: opts.knownAddresses ?? (await savedLanAddresses()),
          sweepSubnet: opts.sweepSubnet,
        });
        // The native layer returns `configurationIssues` on the discovery result, but the
        // plugin's TS `DiscoverPrintersResult` doesn't declare the field yet — read it via a cast.
        const issues =
          (
            result as DiscoverPrintersResult & {
              configurationIssues?: StarPrintConfigurationIssue[];
            }
          ).configurationIssues ?? [];
        return {
          printers: result.printers,
          configurationIssues: issues,
          message: result.message,
          ...(result.diagnostics ? { diagnostics: result.diagnostics } : {}),
        };
      });
    },

    /** Connect to a specific printer (used by the settings UI when picking one). */
    connectPrinter(
      connection: StarPrinterConnection
    ): Promise<{ connectionId: string; printer?: StarPrinter; status?: StarPrinterStatus }> {
      return withLock(async () => {
        const result = await connectRaw(connection);
        if (!result.success || !result.connectionId) {
          throw toError(result, 'Failed to connect to printer');
        }
        return {
          connectionId: result.connectionId,
          printer: result.printer,
          status: result.status,
        };
      });
    },

    /** Keep an unattended settings selection scoped through native I/O and persistence. */
    connectAndSavePrinter(printer: StarPrinter, isCurrent: () => boolean): Promise<boolean> {
      return withLock(async () => {
        if (!isCurrent()) return false;
        const previous = await getDefaultStarPrinterFromDB();
        if (!isCurrent()) return false;
        let committed = false;
        let wroteDefault = false;
        try {
          const result = await connectRaw(printer.connection);
          if (!result.success || !result.connectionId)
            throw toError(result, 'Failed to connect to printer');
          if (!isCurrent()) return false;
          const selected = toSavedPrinter(printer);
          wroteDefault = true;
          printerSetupUnconfirmed = true;
          await saveDefaultStarPrinterToDB(selected);
          const saved = await getDefaultStarPrinterFromDB();
          if (JSON.stringify(saved) !== JSON.stringify(selected))
            throw new StarServiceError('SAVE_FAILED', 'The receipt printer could not be saved.');
          if (!isCurrent()) return false;
          printerSetupUnconfirmed = false;
          committed = true;
          return true;
        } finally {
          if (!committed) {
            try {
              if (wroteDefault) {
                if (previous) await saveDefaultStarPrinterToDB(previous);
                else await deleteDefaultStarPrinterFromDB();
                const restored = await getDefaultStarPrinterFromDB();
                printerSetupUnconfirmed = JSON.stringify(restored) !== JSON.stringify(previous);
              }
            } finally {
              // Native connect cannot be aborted after dispatch. Never let its late socket
              // route a later receipt: the next job reconnects the confirmed saved default.
              await disconnectRaw();
            }
          }
        }
      });
    },

    /**
     * Check a printer that is **not yet saved** — the "does it answer?" step of
     * Add Printer. Connects, records what the printer says about itself and
     * releases it again in one locked step (`withCandidate`), so live receipts
     * never reach it and its status never stands in for the saved printer's.
     */
    checkCandidate(connection: StarPrinterConnection): Promise<StarCandidateCheck> {
      return withCandidate<StarCandidateCheck>(
        connection,
        async (_impl, _connectionId, connected) => ({
          success: true,
          printer: connected.printer,
          status: connected.status,
          message: connected.message,
        }),
        (connected) => ({ success: false, message: connected.message, error: connected.error })
      );
    },

    /**
     * Print a bitmap on a printer that is **not yet saved** — the "test before
     * you save" step of Add Printer — or on a saved printer that is not the
     * active one.
     *
     * It connects to `connection` explicitly, prints on that connection id only
     * and releases it again in one locked step (`withCandidate`). Unlike every
     * other print method it has **no reconnect-from-default fallback**: a test
     * page meant for one printer must never come out of the register's current
     * printer instead. It does not touch payment state in any way; a failure
     * here is only ever a printer failure.
     */
    printTestImageTo(connection: StarPrinterConnection, imageBase64: string): Promise<StarPrintResult> {
      return withCandidate(
        connection,
        (impl, connectionId) =>
          impl.printImage({ connectionId, imageBase64, cutPaper: true, timeoutMs: OP_TIMEOUT_MS }),
        (connected) => connected
      );
    },

    /** Drop the active connection (best-effort) and clear cached state. */
    disconnectPrinter(): Promise<void> {
      return withLock(() => disconnectRaw());
    },

    /** Connection id of the live connection, or null. Synchronous, for UI hints. */
    getActiveConnectionId(): string | null {
      return activeConnectionId;
    },

    /** Read printer status, reconnecting from the saved default if needed. */
    getPrinterStatus(opts: { forceReconnect?: boolean } = {}): Promise<StarPrinterStatus> {
      return withLock(async () => {
        if (opts.forceReconnect) await disconnectRaw();
        try {
          return await readStatus(await ensureConnectedRaw());
        } catch (err) {
          if (isStaleError(err)) {
            activeConnectionId = null;
            return readStatus(await ensureConnectedRaw());
          }
          throw err;
        }
      });
    },

    /** Last status seen via getPrinterStatus or a status-changed event. */
    getLastStatus(): StarPrinterStatus | null {
      return lastStatus;
    },

    /**
     * Subscribe to live status changes. Registers the plugin listeners lazily and
     * immediately replays the last known status. Returns an unsubscribe function.
     */
    async addStatusListener(
      cb: (status: StarPrinterStatus, connectionId?: string) => void
    ): Promise<() => void> {
      statusListeners.add(cb);
      await ensurePluginListeners();
      if (lastStatus) cb(lastStatus, activeConnectionId ?? undefined);
      return () => {
        statusListeners.delete(cb);
      };
    },

    /** Print a receipt built from `StarPrintReceiptItem[]`. */
    printReceipt(
      items: StarPrintReceiptItem[],
      opts: PrintReceiptOptions = {}
    ): Promise<StarPrintOperationResult> {
      return withLock(async () => {
        const impl = await backend();
        return withConnectionRaw(
          (connectionId) =>
            impl.printReceipt({
            connectionId,
            items,
            cutPaper: opts.cutPaper ?? true,
            openCashDrawer: opts.openCashDrawer,
            copies: opts.copies,
            jobId: opts.jobId,
            timeoutMs: OP_TIMEOUT_MS,
          }),
          opts.isCurrent
        );
      });
    },

    /**
     * Print a pre-rendered bitmap (a base64 PNG/JPEG, WITHOUT the `data:` URL
     * prefix). Mirrors `printReceipt`: serialized through the lock and routed at
     * the active connection, reconnecting+retrying once on a stale connection.
     */
    printImage(imageBase64: string, opts: PrintImageOptions = {}): Promise<StarPrintOperationResult> {
      return withLock(async () => {
        const impl = await backend();
        return withConnectionRaw(
          (connectionId) =>
            impl.printImage({
            connectionId,
            imageBase64,
            width: opts.width,
            cutPaper: opts.cutPaper ?? true,
            openCashDrawer: opts.openCashDrawer,
            jobId: opts.jobId,
            timeoutMs: OP_TIMEOUT_MS,
          }),
          opts.isCurrent
        );
      });
    },

    /** Kick the cash drawer. */
    openCashDrawer(opts: { channel?: 'drawer1' | 'drawer2' } = {}): Promise<StarPrintResult> {
      return withLock(async () => {
        const impl = await backend();
        return withConnectionRaw((connectionId) =>
          impl.openCashDrawer({
            connectionId,
            channel: opts.channel,
            timeoutMs: OP_TIMEOUT_MS,
          })
        );
      });
    },

    /** Persist `printer` as the default (stores the full connection for reconnect). */
    async saveDefaultPrinter(printer: StarPrinter): Promise<void> {
      await saveDefaultStarPrinterToDB(toSavedPrinter(printer));
    },

    /** Read the persisted default printer, or null. */
    getDefaultPrinter(): Promise<SavedStarPrinter | null> {
      return getDefaultStarPrinterFromDB();
    },

    /** Forget the default printer and disconnect. */
    async clearDefaultPrinter(): Promise<void> {
      await deleteDefaultStarPrinterFromDB();
      await withLock(() => disconnectRaw());
    },

    /**
     * Can this register really print, and with what backend.
     *
     * **This replaces the plugin's `isNativeAvailable()`**, which returns
     * `platform: 'android' | 'ios' | 'web'` and therefore has no honest value on
     * a desktop register — `'web'` is the value that means "you are looking at
     * the mock", which is the exact claim hazard F2 is about. The question is
     * answered from PointCraft's capability table instead; the plugin call
     * survives inside the mobile backend, where it is truthful.
     */
    async getAvailability(): Promise<PrinterAvailability> {
      return (await backend()).describeAvailability();
    },

    /**
     * Read OS Bluetooth authorization SILENTLY — never instantiates a Bluetooth
     * manager, so it does not trigger the iOS permission prompt. No `withLock`:
     * it touches no connection and contends with nothing.
     */
    async getBluetoothAuthorization(): Promise<BluetoothAuthorizationResult> {
      return (await backend()).getBluetoothAuthorization();
    },

    /**
     * Dev-only: drive the browser mock's reported status. A no-op anywhere the
     * capability table does not say mock controls exist.
     *
     * The plugin is imported **here, inside the guard**, and nowhere else in
     * this module. `setMockMode` is deliberately absent from `StarPrintBackend`
     * (PLAN §5.4), so a desktop register has no path to it even by accident —
     * this branch is unreachable on Electron, where `mockControlsEnabled` is
     * `false`.
     */
    async setWebMockStatus(
      status: 'online' | 'offline' | 'paper_empty' | 'cover_open'
    ): Promise<void> {
      if (!getPlatformPrinter().mockControlsEnabled) return;
      const { StarPrint } = await import('@am3nd-org/capacitor-starprint-plugin');
      await StarPrint.setMockMode({ status });
    },
  };
}

export const starPrintService = createStarPrintService();
